import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface IdeaManagementProps {
  selectedBrand: string | null
}

const IdeaManagement: React.FC<IdeaManagementProps> = ({ selectedBrand }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingIdea, setEditingIdea] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const {
    data: ideas,
    isLoading,
    refetch,
  } = trpc.contentManagement.listIdeas.useQuery({
    search: searchQuery || undefined,
    // tags: selectedTags.length > 0 ? selectedTags : undefined, // TODO: Add tags support to backend
    limit: 50,
  })

  const createIdeaMutation = trpc.contentManagement.createIdea.useMutation()
  const updateIdeaMutation = trpc.contentManagement.updateIdea.useMutation()
  const generateContentMutation = trpc.contentManagement.createContentFromIdea.useMutation()

  const handleCreateIdea = async (ideaData: any) => {
    try {
      await createIdeaMutation.mutateAsync(ideaData)
      setIsCreateModalOpen(false)
      void refetch()
    } catch (error) {
      console.error("Failed to create idea:", error)
    }
  }

  const handleUpdateIdea = async (ideaId: string, updates: any) => {
    try {
      await updateIdeaMutation.mutateAsync({ ideaId, ...updates })
      setEditingIdea(null)
      void refetch()
    } catch (error) {
      console.error("Failed to update idea:", error)
    }
  }

  const handleGenerateContent = async (ideaId: string, platform: string) => {
    try {
      await generateContentMutation.mutateAsync({ ideaId, platform, brandId: selectedBrand || "" })
      // Optionally show success message or navigate to content creation
    } catch (error) {
      console.error("Failed to generate content:", error)
    }
  }

  // Extract all unique tags from ideas for filtering
  const allTags = React.useMemo(() => {
    const tags = new Set<string>()
    ideas?.forEach((idea: any) => {
      if (idea.idea.tags) {
        idea.idea.tags.forEach((tag: string) => tags.add(tag))
      }
    })
    return Array.from(tags)
  }, [ideas])

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Idea Management</h2>
          <p className="">Organize and develop your content ideas</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Idea
        </button>
      </div>

      {/* Filters */}
      <div className="bg-background p-4 rounded-lg shadow">
        <div className="space-y-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search ideas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tag Filters */}
          {allTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Filter by Tags:</label>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag) ? "bg-blue-600 text-white" : "bg-foregroundx text-primary "
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {selectedTags.length > 0 && (
                <button onClick={() => setSelectedTags([])} className="mt-2 text-sm  hover:text-blue-800">
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Ideas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ideas?.map((item: any) => (
          <div key={item.idea.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-medium  line-clamp-2">{item.idea.title}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {item.brand ? `${item.brand.name}` : "No brand"} •
                  <span
                    className={`ml-1 ${
                      item.idea.priority === "high"
                        ? "text-destructive"
                        : item.idea.priority === "medium"
                          ? "text-yellow-600"
                          : "text-green-600"
                    }`}
                  >
                    {item.idea.priority} priority
                  </span>
                </p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setEditingIdea(item.idea)} className="text-gray-400 hover:">
                  ✏️
                </button>
              </div>
            </div>

            {item.idea.description && <p className="text-primary text-sm mb-4 line-clamp-3">{item.idea.description}</p>}

            {/* Tags */}
            {item.idea.tags && item.idea.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {item.idea.tags.slice(0, 3).map((tag: string, idx: number) => (
                  <span key={idx} className="px-2 py-1 bg-foregroundx text-primary text-xs rounded">
                    {tag}
                  </span>
                ))}
                {item.idea.tags.length > 3 && (
                  <span className="px-2 py-1 bg-foregroundx text-gray-500 text-xs rounded">
                    +{item.idea.tags.length - 3}
                  </span>
                )}
              </div>
            )}

            {/* Domains */}
            {item.idea.domains && Object.keys(item.idea.domains).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-1">Relevant domains:</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(item.idea.domains)
                    .slice(0, 2)
                    .map(([domain, score]: [string, any]) => (
                      <span key={domain} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {domain} ({Math.round(score * 100)}%)
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Usage Stats */}
            <div className="flex justify-between text-xs text-gray-500 mb-4">
              <span>Used: {item.idea.usageCount || 0} times</span>
              <span>Created: {new Date(item.idea.createdAt).toLocaleDateString()}</span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleGenerateContent(item.idea.id, "linkedin")}
                  disabled={generateContentMutation.isLoading}
                  className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded text-sm hover:bg-blue-200 transition-colors disabled:opacity-50"
                >
                  💼 LinkedIn
                </button>
                <button
                  onClick={() => handleGenerateContent(item.idea.id, "twitter")}
                  disabled={generateContentMutation.isLoading}
                  className="flex-1 bg-foregroundx text-primary py-2 px-3 rounded text-sm  transition-colors disabled:opacity-50"
                >
                  🐦 Twitter
                </button>
              </div>
              <button
                onClick={() => setEditingIdea(item.idea)}
                className="w-full bg-foregroundx text-primary py-2 px-3 rounded text-sm  transition-colors"
              >
                Edit Idea
              </button>
            </div>
          </div>
        ))}

        {(!ideas || ideas.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">💡</div>
            <h3 className="text-lg font-medium  mb-2">No ideas yet</h3>
            <p className=" mb-4">
              {selectedTags.length > 0 || searchQuery
                ? "No ideas match your current filters. Try adjusting your search or tags."
                : "Create your first idea or approve some scraped content to get started."}
            </p>
            {selectedTags.length === 0 && !searchQuery && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Idea
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Idea Modal */}
      {isCreateModalOpen && (
        <CreateIdeaModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateIdea}
          isLoading={createIdeaMutation.isLoading}
          selectedBrand={selectedBrand}
        />
      )}

      {/* Edit Idea Modal */}
      {editingIdea && (
        <EditIdeaModal
          idea={editingIdea}
          onClose={() => setEditingIdea(null)}
          onSubmit={(updates) => handleUpdateIdea(editingIdea.id, updates)}
          isLoading={updateIdeaMutation.isLoading}
        />
      )}
    </div>
  )
}

const CreateIdeaModal: React.FC<{
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
  selectedBrand: string | null
}> = ({ onClose, onSubmit, isLoading, selectedBrand }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    tags: [] as string[],
    priority: "medium",
    brandId: selectedBrand || "",
  })
  const [newTag, setNewTag] = useState("")

  const { data: brands } = trpc.contentManagement.listBrands.useQuery()

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }))
      setNewTag("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Create New Idea</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="Brief, descriptive title for the idea"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Detailed description of the idea, key points, angles, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Brand</label>
            <select
              value={formData.brandId}
              onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific brand</option>
              {brands?.map((brand: any) => (
                <option key={brand.brand.id} value={brand.brand.id}>
                  {brand.brand.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Tags</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Add tag..."
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-foregroundx text-primary text-sm rounded-full flex items-center"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-destructive hover:text-destructive"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Creating..." : "Create Idea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditIdeaModal: React.FC<{
  idea: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ idea, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    title: idea.title,
    description: idea.description || "",
    tags: idea.tags || [],
    priority: idea.priority,
  })
  const [newTag, setNewTag] = useState("")

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }))
      setNewTag("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Edit Idea</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Tags</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Add tag..."
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-foregroundx text-primary text-sm rounded-full flex items-center"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-destructive hover:text-destructive"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Updating..." : "Update Idea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default IdeaManagement
